export type DurationWithUnits = {
  value: number;
  units: string;
};

/**
 * Splits a duration string (e.g. '7h') into an object with value and units (e.g. { value: 7, units: 'h' }).
 * IMPORTANT: Mirrored by _split_duration() in sdk/pypi/seeq/spy/_shared_definition_functions.py for SPy report
 * date-range compatibility. Keep behavior and tests synchronized on both sides.
 *
 * @param durationString - The string to be split
 * @example splitDuration('3 min') // { value: 3, units: 'min' }
 * @returns The value and units, or `undefined` if either could not be determined from the supplied string.
 */
export function splitDuration(durationString: string | undefined): DurationWithUnits | undefined {
  if (typeof durationString !== 'string') {
    return undefined;
  }

  // The value and units are intentionally matched independently and unanchored: the first number and the first run of
  // letters are used wherever they appear (e.g. '5h3m' and 'h5' both yield { value: 5, units: 'h' }).
  const value = durationString.match(/[-+]?[0-9]*\.?[0-9]+/)?.[0];
  if (!value) {
    return undefined;
  }

  const units = durationString.match(/[a-zA-Z]+/)?.[0];
  if (!units) {
    return undefined;
  }

  return { value: +value, units };
}
